import React, { useState, useCallback } from 'react';
import {
  useFloating,
  autoUpdate,
  offset,
  flip,
  shift,
  useHover,
  useFocus,
  useDismiss,
  useRole,
  useInteractions,
  FloatingPortal,
  FloatingDelayGroup,
  useDelayGroup
} from '@floating-ui/react';
import TestNivoBarChart from './TestNivoBarChart';
import { debounce } from 'lodash';

const AnnualWageWithRating = ({ avgAnnualWage, avgHourlyWage, wageRating, jobs }) => {
  const [isTooltipVisible, setTooltipVisible] = useState(false);
  // Start with null so that tooltipPosition is not defined until the mouse moves.
  const [tooltipPosition, setTooltipPosition] = useState(null);

  const { refs, context, placement } = useFloating({
    open: isTooltipVisible,
    onOpenChange: setTooltipVisible,
    placement: 'bottom',
    whileElementsMounted: autoUpdate,
    middleware: [
      offset(5),
      flip({ fallbackPlacements: ['top'] }),
      shift()
    ]
  });

  const { delay } = useDelayGroup(context);
  const hover = useHover(context, { move: true, delay });
  const focus = useFocus(context);
  const dismiss = useDismiss(context);
  const role = useRole(context, { role: 'tooltip' });

  const { getReferenceProps, getFloatingProps } = useInteractions([
    hover,
    focus,
    dismiss,
    role
  ]);

  // Update tooltipPosition on mouse move with a debounced callback.
  const handleMouseMove = useCallback(
    debounce((event) => {
      setTooltipPosition({ x: event.clientX, y: event.clientY });
    }, 10),
    []
  );

  return (
    <div className="w-full">
      <div
        ref={refs.setReference}
        {...getReferenceProps()}
        className="cursor-pointer"
        onMouseMove={handleMouseMove}
      >
        <span
          className={`text-xs pb-1 text-center ${avgAnnualWage ? "text-zinc-700" : "text-zinc-400"}`}
        >
          {avgAnnualWage
            ? `$ ${avgAnnualWage.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ",")}`
            : "N/A"}
        </span>
        <div className="h-[12px] flex mt-1">
          {[...Array(5)].map((_, i) => (
            <div
              key={i}
              className={`w-full h-3 border border-white ${wageRating > i ? "bg-emerald-300" : "bg-zinc-100"} ${i === 0 ? "rounded-l" : i === 4 ? "rounded-r" : ""}`}
            />
          ))}
        </div>
      </div>
      <FloatingPortal>
        {isTooltipVisible && tooltipPosition && (
          <div
            className="bg-white border border-zinc-300 rounded-md p-2 shadow-lg z-50"
            ref={refs.setFloating}
            style={{
              position: 'fixed',
              top:
                placement === 'bottom'
                  ? tooltipPosition.y + 20
                  : tooltipPosition.y - 150,
              left: tooltipPosition.x + 20
            }}
            {...getFloatingProps()}
          >
            <TestNivoBarChart jobs2={jobs} />
          </div>
        )}
      </FloatingPortal>
    </div>
  );
};

const AnnualWageWithRatingWrapper = (props) => (
  <FloatingDelayGroup delay={{ open: 400, close: 0 }}>
    <AnnualWageWithRating {...props} />
  </FloatingDelayGroup>
);

export default AnnualWageWithRatingWrapper;